"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Check, ArrowRight, Flag } from "lucide-react";

const PRICING_EXPERIMENT_ID = "pricing_best_seller_rating_v1";

export type PreviewPlan = {
  data: string;
  validity: number;
  price: string;
  perGb?: string;
  href: string;
  best?: boolean;
};

/**
 * Travel Connected — homepage plan selector preview. Countries-only since the
 * catalog does not contain regional or global plans. Shows 3 plan cards.
 * Client (tab state) — data passed in, no pricing logic here.
 */
export default function PlanSelectorPreview({
  country,
  plans,
}: {
  country: string;
  plans: PreviewPlan[];
}) {
  const { t } = useTranslations();

  const onSelect = (plan: PreviewPlan) => {
    try {
      track({
        event_name: "plan_preview_selected",
        event_id: `plan_preview_selected:${PRICING_EXPERIMENT_ID}:${plan.data}`,
        experiment_id: PRICING_EXPERIMENT_ID,
        items: [{ item_id: country + "-" + plan.data, item_name: country + " " + plan.data }],
      });
    } catch { /* best-effort */ }
  };

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" className="text-center max-w-2xl mx-auto mb-7" y={18}>
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary font-bold text-sm px-4 py-1.5 mb-3">
          <Flag className="w-4 h-4" />
          {country}
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-foreground">{t("pc.plans.title", "Popular data plans for {country}").replace("{country}", country)}</h2>
        <p className="mt-3 text-muted-foreground">{t("pc.plans.sub", "Instant eSIM delivery · Keep your number · No roaming fees")}</p>
      </Reveal>

      {plans.length > 0 ? (
        <Stagger as="div" className="grid md:grid-cols-3 gap-5 max-w-4xl mx-auto" staggerChildren={0.09} amount={0.25}>
          {plans.map((plan) => (
            <StaggerItem as="div" key={plan.data} y={20} className={`pc-card p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${plan.best ? "pc-best ring-2 ring-primary relative" : "hover:border-primary/40"}`}>
              {plan.best && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-wide px-4 py-1 shadow-lg">
                  {t("pc.plans.best", "Best value")}
                </span>
              )}
              <div className="text-2xl font-extrabold text-foreground mb-1">{plan.data}</div>
              <div className="text-sm text-muted-foreground mb-4">{plan.validity} {t("pc.plans.days", "days")}</div>
              <div className="text-3xl font-black text-foreground mb-1">{plan.price}</div>
              {plan.perGb && <div className="text-xs text-muted-foreground mb-4">{plan.perGb} / GB</div>}
              {!plan.perGb && <div className="mb-4" />}
              <Link href={plan.href} onClick={() => onSelect(plan)} className={`mt-auto inline-flex items-center justify-center gap-2 rounded-full h-11 font-bold text-sm transition-all active:scale-[0.98] group/sel ${plan.best ? "bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90" : "border border-border text-foreground hover:border-primary/50"}`}>
                {t("pc.plans.select", "Select plan")} <ArrowRight className="w-4 h-4 transition-transform group-hover/sel:translate-x-0.5" />
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      ) : (
        <div className="pc-card max-w-2xl mx-auto p-8 text-center">
          <h3 className="text-xl font-bold text-foreground">{t("pc.plans.none", "No plans available for this destination right now.")}</h3>
        </div>
      )}
    </section>
  );
}
